import { format, unformat } from '@react-input/mask';
import { useEffect } from 'react';
import { FieldPath, FieldValues, useFormContext, useWatch } from 'react-hook-form';
import { MaskedInput } from '../MaskedInput/MaskedInput';
import { FormFieldProps } from '../interfaces';

interface Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends FormFieldProps<TFieldValues, TName> {
  maxDigits?: number;
}
export const MoneyInput = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: Props<TFieldValues, TName>
) => {
  const {
    name,
    description,
    disabled,
    cyTag,
    label,
    className,
    maxDigits = 9,
    flagged,
    required,
    tooltip,
    errorCyTag,
    reserveFeedbackSpace,
  } = props;
  const mask = '$' + '#'.repeat(maxDigits);
  const { setValue, getValues } = useFormContext<TFieldValues>();
  const formattedValue = useWatch({ name: `${name}Formatted` });
  const options = { mask: mask, replacement: { '#': /\d/ } };

  useEffect(() => {
    const oldValue = getValues(name);
    if (oldValue === undefined || oldValue === null) return;
    const newValue = format(Number(oldValue).toString(), options);
    setValue(`${name}Formatted` as unknown as TName, newValue as unknown as TFieldValues[TName]);
  }, []);

  useEffect(() => {
    if (!formattedValue) {
      setValue(name, 0 as unknown as TFieldValues[TName]);
      return;
    }
    setValue(name, Number(unformat(formattedValue, options)) as unknown as TFieldValues[TName]);
  }, [formattedValue]);

  return (
    <MaskedInput
      name={`${name}Formatted`}
      label={label}
      cyTag={cyTag}
      showMask={false}
      disabled={disabled}
      className={className}
      description={description}
      flagged={flagged}
      required={required}
      tooltip={tooltip}
      errorCyTag={errorCyTag}
      reserveFeedbackSpace={reserveFeedbackSpace}
      {...options}
    />
  );
};
